import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { screenApi } from '../lib/api'
import { setScreenToken, setRoomId, setTenantInfo, setScreenInfo } from '../lib/storage'
import { AgaruLogo } from '../components/brand/AgaruLogo'

interface Props {
  onPaired: () => void
}

export default function PairingScreen({ onPaired }: Props) {
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shake, setShake] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (code.length < 6) {
      triggerError('Please enter a 6-character pairing code')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const res = await screenApi.pair(code.toUpperCase())
      const { token, screen } = res.data

      setScreenToken(token)
      if (screen.roomId) setRoomId(screen.roomId)
      if ((screen as any).tenant) {
        setTenantInfo({
          name: (screen as any).tenant.name,
          primaryColor: (screen as any).tenant.primaryColor,
        })
      }
      // Persist human-readable screen identity so the running app can surface
      // "Main Studio · Ring 2" in the chrome. `screen.name` is always set (the
      // admin names the screen when creating it); `room.name` is best-effort.
      setScreenInfo({
        screenName: (screen as any).name ?? 'TV',
        roomName: (screen as any).room?.name ?? null,
      })

      onPaired()
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? 'Invalid pairing code. Please try again.'
      triggerError(typeof msg === 'string' ? msg : msg.join(', '))
    } finally {
      setIsLoading(false)
    }
  }

  const triggerError = (msg: string) => {
    setError(msg)
    setShake(true)
    setTimeout(() => setShake(false), 600)
  }

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)
    setCode(val)
    if (error) setError(null)
  }

  return (
    <div className="w-screen h-screen bg-[#0E1022] flex flex-col items-center justify-center relative overflow-hidden">
      {/* Ambient background gradient */}
      <div className="absolute inset-0 bg-gradient-radial from-[#191C33]/60 via-transparent to-transparent pointer-events-none" />
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(99,102,241,0.15) 0%, transparent 70%)',
        }}
      />

      {/* Logo / Brand */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-16 text-center"
      >
        <div className="mb-4">
          <AgaruLogo size={72} />
        </div>
        <div className="text-white/30 text-xl font-light tracking-widest uppercase">
          TV Display Setup
        </div>
      </motion.div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="relative z-10 w-full max-w-lg"
      >
        <motion.form
          onSubmit={handleSubmit}
          animate={shake ? { x: [-10, 10, -6, 6, -3, 3, 0] } : { x: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-[#191C33] border border-[#31365A] rounded-3xl p-12 shadow-2xl"
        >
          <h1 className="text-3xl font-bold text-white text-center mb-2">
            Enter Pairing Code
          </h1>
          <p className="text-white/40 text-center mb-10 text-lg">
            Find this code in the Agaru admin panel under Screens
          </p>

          {/* Code Input */}
          <div className="mb-8">
            <input
              ref={inputRef}
              type="text"
              value={code}
              onChange={handleInput}
              maxLength={6}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              placeholder="AB12CD"
              className="
                w-full text-center text-5xl font-black tracking-[0.4em]
                bg-[#252945] border-2 rounded-2xl
                px-6 py-6 text-white placeholder-white/20
                outline-none transition-all duration-200
                focus:border-orange-500 focus:bg-[#252945]
                border-[#31365A]
              "
              style={{ fontVariantNumeric: 'tabular-nums' }}
            />

            {/* Character dots indicator */}
            <div className="flex justify-center gap-3 mt-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className={`
                    w-3 h-3 rounded-full transition-all duration-200
                    ${i < code.length ? 'bg-orange-500 scale-125' : 'bg-white/15'}
                  `}
                />
              ))}
            </div>
          </div>

          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6 overflow-hidden"
              >
                <p className="text-red-400 text-center text-lg bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                  {error}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || code.length < 6}
            className="
              w-full py-5 rounded-2xl text-xl font-bold tracking-wide
              transition-all duration-200
              bg-orange-500 text-white hover:bg-orange-600 active:scale-98
              disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-orange-500
              focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-[#191C33
            "
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-3">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Pairing…
              </span>
            ) : (
              'Pair Screen'
            )}
          </button>
        </motion.form>
      </motion.div>

      {/* Bottom brand */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="absolute bottom-8 right-10 text-white/20 text-sm font-medium"
      >
        Agaru TV v1.0
      </motion.p>
    </div>
  )
}
